/**
 * Modal de confirmação para suspend/activate — TH-53159.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";

interface Tenant {
  id: string;
  name: string;
  status: string;
}

type Action = "suspend" | "activate";

interface ConfirmStatusDialogProps {
  tenant: Tenant;
  action: Action;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

async function callAction(tenantId: string, action: Action): Promise<void> {
  const res = await fetch(`/api/tenants/${tenantId}/${action}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

export function ConfirmStatusDialog({
  tenant,
  action,
  open,
  onOpenChange,
}: ConfirmStatusDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => callAction(tenant.id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      onOpenChange(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const isDestructive = action === "suspend";
  const verb = action === "suspend" ? "Suspender" : "Ativar";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setError(null);
      }}
    >
      <DialogContent>
        <DialogTitle>{verb} cliente?</DialogTitle>
        <DialogDescription>
          Confirmar {verb.toLowerCase()} de <strong>{tenant.name}</strong>?
          {isDestructive ? (
            <span className="mt-2 block text-[var(--color-destructive)]">
              Tenant suspenso não consegue acessar o sistema. A ação é
              reversível por <em>Ativar</em>.
            </span>
          ) : null}
        </DialogDescription>

        {error ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant={isDestructive ? "destructive" : "primary"}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Processando…" : verb}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
